import React, { useMemo, useState } from "react";
import { Country, City } from "country-state-city";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "./ui/command";
import { useTranslation } from "react-i18next";

/**
 * Emoji flag from ISO2 country code (e.g. "NG" -> 🇳🇬)
 */
function flag(iso2) {
  if (!iso2 || iso2.length !== 2) return "🏳️";
  return String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
}

/**
 * CountryCityPicker
 * Props:
 *   country: { name, iso2 } | null
 *   city: string
 *   onChange: ({ country, city }) => void
 *   dark: boolean (default true) — applies dark surface classes
 */
export default function CountryCityPicker({ country, city, onChange, dark = true }) {
  const { t } = useTranslation();
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  const countries = useMemo(() => Country.getAllCountries().map((c) => ({ name: c.name, iso2: c.isoCode })), []);
  const cities = useMemo(() => {
    if (!country?.iso2) return [];
    return City.getCitiesOfCountry(country.iso2) || [];
  }, [country?.iso2]);

  const surface = dark ? "bg-[#0f0f0f] border-white/20 text-white hover:bg-white/5" : "";
  const contentSurface = dark ? "bg-[#0f0f0f] border-white/10 text-white p-0" : "p-0";

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* COUNTRY */}
      <div>
        <label className="uppercase text-xs tracking-widest text-white/70">{t("common.country")}</label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="country-picker-trigger"
              className={cn(
                "mt-2 w-full flex items-center justify-between h-11 px-3 rounded-sm border transition-colors focus:border-[#e65f2b] outline-none",
                surface
              )}
            >
              <span className="flex items-center gap-2 truncate">
                <span className="text-lg leading-none">{country?.iso2 ? flag(country.iso2) : "🌍"}</span>
                <span className="truncate">{country?.name || t("common.country")}</span>
              </span>
              <ChevronsUpDown size={14} className="opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className={cn("w-[--radix-popover-trigger-width]", contentSurface)} align="start">
            <Command className="bg-transparent">
              <CommandInput placeholder={`${t("common.search")}…`} data-testid="country-picker-search" className="text-white" />
              <CommandList>
                <CommandEmpty className="text-white/60">{t("players.empty")}</CommandEmpty>
                {countries.map((c) => (
                  <CommandItem
                    key={c.iso2}
                    value={c.name}
                    data-testid={`country-option-${c.iso2}`}
                    onSelect={() => {
                      onChange({ country: c, city: "" });
                      setCountryOpen(false);
                    }}
                    className="text-white data-[selected=true]:bg-[#e65f2b]/20 data-[selected=true]:text-[#e65f2b]"
                  >
                    <span className="text-lg mr-2">{flag(c.iso2)}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    {country?.iso2 === c.iso2 && <Check size={14} className="text-[#e65f2b]" />}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* CITY */}
      <div>
        <label className="uppercase text-xs tracking-widest text-white/70">{t("common.city")}</label>
        <Popover open={cityOpen} onOpenChange={(o) => country?.iso2 && setCityOpen(o)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!country?.iso2}
              data-testid="city-picker-trigger"
              className={cn(
                "mt-2 w-full flex items-center justify-between h-11 px-3 rounded-sm border transition-colors focus:border-[#e65f2b] outline-none disabled:opacity-50",
                surface
              )}
            >
              <span className="flex items-center gap-2 truncate">
                <MapPin size={14} className="opacity-50" />
                <span className="truncate">{city || (country?.iso2 ? t("common.city") : t("common.country"))}</span>
              </span>
              <ChevronsUpDown size={14} className="opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className={cn("w-[--radix-popover-trigger-width]", contentSurface)} align="start">
            <Command className="bg-transparent">
              <CommandInput placeholder={`${t("common.search")}…`} data-testid="city-picker-search" className="text-white" />
              <CommandList>
                <CommandEmpty className="text-white/60">
                  {cities.length === 0 ? t("players.empty") : t("players.empty")}
                </CommandEmpty>
                {cities.slice(0, 500).map((c) => (
                  <CommandItem
                    key={`${c.name}-${c.stateCode || ""}-${c.latitude || ""}`}
                    value={c.name}
                    data-testid={`city-option-${c.name}`}
                    onSelect={() => {
                      onChange({ country, city: c.name });
                      setCityOpen(false);
                    }}
                    className="text-white data-[selected=true]:bg-[#e65f2b]/20 data-[selected=true]:text-[#e65f2b]"
                  >
                    <span className="flex-1 truncate">{c.name}</span>
                    {c.stateCode && <span className="text-xs text-white/40 ml-2">{c.stateCode}</span>}
                    {city === c.name && <Check size={14} className="text-[#e65f2b] ml-2" />}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export { flag };
